import api from './api';
import { getEcho, disconnectEcho } from './echo';
import { getSubdomain } from '../helpers/domain';
import { getUser } from './auth';

// Feed do sino: menções do CRM e comunicados da plataforma, na mesma lista.
// O websocket (Reverb) só sinaliza que houve mudança; ao receber o evento
// re-buscamos a lista via REST — a mesma fonte de verdade do carregamento
// inicial. Sem websocket, cai num polling espaçado (fallback degradado).

const RETRY_DELAY = 30000;
const DEGRADED_POLL_INTERVAL = 60000;

// Mesmo com websocket, um comunicado agendado entra no ar sem que nada seja
// empurrado (a publicação já aconteceu, a data é que virou) — daí o refresh
// lento de fundo.
const BACKGROUND_REFRESH_INTERVAL = 300000;

let state = { notifications: [], unreadCount: 0 };
let started = false;
let hadConnection = false;
let retryTimer = null;
let degradedTimer = null;
let backgroundTimer = null;
const listeners = new Set();

// Impressões já reportadas nesta sessão: o mesmo comunicado aparece no sino a
// cada abertura do dropdown, mas "visualizou" é uma vez por pessoa.
const reportedSeen = new Set();

const notify = () => listeners.forEach((listener) => listener(state));

const setState = (next) => {
    state = next;
    notify();
};

const fetchNotifications = () =>
    api.get('/notifications')
        .then((res) => setState({
            notifications: res.data.resources ?? [],
            unreadCount: res.data.unread_count ?? 0,
        }))
        .catch(() => {
            clearTimeout(retryTimer);
            retryTimer = setTimeout(fetchNotifications, RETRY_DELAY);
        });

const startDegradedPolling = () => {
    clearInterval(degradedTimer);
    degradedTimer = setInterval(fetchNotifications, DEGRADED_POLL_INTERVAL);
};

const start = () => {
    if (started) return;
    started = true;

    fetchNotifications();

    clearInterval(backgroundTimer);
    backgroundTimer = setInterval(fetchNotifications, BACKGROUND_REFRESH_INTERVAL);

    try {
        const echo = getEcho();
        const adminId = getUser()?.id;
        if (!echo || !adminId) {
            startDegradedPolling();
            return;
        }

        const subdomain = getSubdomain();

        echo.private(`tenant.${subdomain}.admin.${adminId}.notifications`)
            .listen('.mention-notified', () => fetchNotifications());

        echo.private(`tenant.${subdomain}.platform-notifications`)
            .listen('.platform-notification-published', () => fetchNotifications());

        // Reconexão do websocket: re-sincroniza, pois eventos ocorridos
        // durante a queda não são reenviados pelo canal.
        echo.connector?.pusher?.connection?.bind('connected', () => {
            if (hadConnection) fetchNotifications();
            hadConnection = true;
        });
    } catch (error) {
        console.error('Falha ao assinar canal de notificações em tempo real.', error);
        startDegradedPolling();
    }
};

export const getNotifications = () => state;

// Formato subscribe/getSnapshot esperado por useSyncExternalStore.
export const subscribeNotifications = (listener) => {
    start();
    listeners.add(listener);
    listener(state);
    return () => {
        listeners.delete(listener);
    };
};

// Re-busca imediata após uma ação local (marcar como lida) — não espera o push.
export const refreshNotifications = () => fetchNotifications();

/**
 * Impressão dos comunicados que apareceram na lista. Não re-busca o feed: ver
 * na lista não muda o que o sino mostra, só alimenta o relatório.
 */
export const reportPlatformSeen = (ids) => {
    const pending = (ids ?? []).filter((id) => !reportedSeen.has(id));

    if (pending.length === 0) return Promise.resolve();

    pending.forEach((id) => reportedSeen.add(id));

    return api.post('/notifications/platform/seen', { ids: pending })
        .catch(() => pending.forEach((id) => reportedSeen.delete(id)));
};

export const resetNotifications = () => {
    clearTimeout(retryTimer);
    clearInterval(degradedTimer);
    clearInterval(backgroundTimer);
    retryTimer = null;
    degradedTimer = null;
    backgroundTimer = null;
    reportedSeen.clear();
    disconnectEcho();
    started = false;
    hadConnection = false;
    setState({ notifications: [], unreadCount: 0 });
};
